import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Input, Select, Button, Space, Typography, Tooltip } from 'antd';
import { SearchOutlined, ClearOutlined, UndoOutlined } from '@ant-design/icons';
import {
  jumpSearch,
  testJumpSearch,
  type GraphData,
} from './utils/jumpSearchAlgorithm';

// 扩展Window接口
declare global {
  interface Window {
    OPMSearchFilter?: any;
  }
}

const { Text } = Typography;
const { Option } = Select;

interface SearchPanelProps {
  graphData: { nodes: any[]; edges: any[] };
  lfRef: React.MutableRefObject<any>;
  onSaveViewState?: () => void;
  onRestoreViewState?: () => void;
  onResetView?: () => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  graphData,
  lfRef,
  onSaveViewState,
  onRestoreViewState,
  onResetView,
}) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [jumpCount, setJumpCount] = useState(1);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currentSearchResult, setCurrentSearchResult] = useState<any>(null);
  const [isInSearchMode, setIsInSearchMode] = useState(false);
  const [originalViewState, setOriginalViewState] = useState<any>(null);

  // 维护一份完整的图数据缓存，不受搜索影响
  const [fullGraphData, setFullGraphData] = useState<{
    nodes: any[];
    edges: any[];
  }>({ nodes: [], edges: [] });

  const searchInputRef = useRef<any>(null);

  // 获取节点显示名称
  const getDisplayName = (node: any): string => {
    return (
      node.text?.value ||
      node.text ||
      node.properties?.name ||
      node.id ||
      '未命名节点'
    );
  };

  // 获取节点类型颜色
  const getNodeTypeColor = (nodeType: string) => {
    const colors = {
      rect: '#1890ff',
      circle: '#52c41a',
      diamond: '#fa8c16',
      polygon: '#722ed1',
      state: '#13c2c2',
      'dynamic-group': '#eb2f96',
    };
    return colors[nodeType as keyof typeof colors] || '#666';
  };

  // 获取节点类型名称
  const getNodeTypeName = (nodeType: string) => {
    const types = {
      rect: '矩形',
      circle: '圆形',
      diamond: '菱形',
      polygon: '多边形',
      state: '状态',
      'dynamic-group': '分组',
    };
    return types[nodeType as keyof typeof types] || nodeType;
  };

  // 搜索处理 - 联想搜索
  const handleSearch = () => {
    if (!searchKeyword.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const keyword = searchKeyword.toLowerCase().trim();
    console.log('搜索关键词:', keyword);

    const results = graphData.nodes
      .filter(node => {
        // 获取所有可能的搜索字段
        const searchFields = [
          node.text?.value,
          node.text,
          node.properties?.name,
          node.properties?.description,
          node.id,
        ];

        // 检查任意字段是否包含关键词
        return searchFields.some(field => {
          if (!field) return false;
          const fieldStr = String(field).toLowerCase();
          return fieldStr.includes(keyword);
        });
      })
      .slice(0, 10); // 最多显示10个结果

    console.log('搜索结果:', results);
    setSearchResults(results);
    setShowSearchResults(true);
  };

  // 搜索框失焦处理
  const handleSearchBlur = () => {
    // 延迟隐藏，让点击事件能够触发
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200);
  };

  // 更新完整图数据缓存
  const updateFullGraphData = () => {
    if (!lfRef.current) return;

    const currentData = lfRef.current.getGraphData();
    setFullGraphData({
      nodes: [...currentData.nodes],
      edges: [...currentData.edges],
    });

    console.log(
      '更新完整图数据缓存，节点数:',
      currentData.nodes.length,
      '边数:',
      currentData.edges.length
    );
  };

  // 保存当前视图状态（在进入搜索模式前调用）
  const saveCurrentViewState = () => {
    if (!lfRef.current) return;

    // 首先更新完整数据缓存
    updateFullGraphData();

    // 保存当前状态作为原始状态
    setOriginalViewState({
      nodes: [...fullGraphData.nodes],
      edges: [...fullGraphData.edges],
    });

    console.log('保存搜索前视图状态');
  };

  // 还原到搜索前的视图状态
  const restoreOriginalView = () => {
    if (!lfRef.current || !originalViewState) return;

    console.log('还原到搜索前状态');

    // 还原完整的图数据
    lfRef.current.render({
      nodes: originalViewState.nodes,
      edges: originalViewState.edges,
    });

    // 重置搜索状态
    setIsInSearchMode(false);
    setCurrentSearchResult(null);
    setOriginalViewState(null);

    console.log('视图已还原，显示所有节点');
  };

  // 获取分组的所有子节点
  const getGroupChildren = (groupId: string): string[] => {
    const children: string[] = [];
    const dataToUse =
      fullGraphData.nodes.length > 0
        ? fullGraphData
        : lfRef.current?.getGraphData() || { nodes: [] };

    console.log(
      `🔍 查找分组 ${groupId} 的子节点，总节点数: ${dataToUse.nodes.length}`
    );

    dataToUse.nodes.forEach((node: any) => {
      // 检查节点是否属于该分组（支持多种分组属性）
      if (
        node.group === groupId ||
        node.parentId === groupId ||
        node.groupId === groupId
      ) {
        children.push(node.id);
        console.log(`📁 找到子节点: ${node.id} (type: ${node.type})`);
      }
    });

    console.log(
      `🎯 分组 ${groupId} 共有 ${children.length} 个子节点:`,
      children
    );
    return children;
  };

  // 检查节点是否为分组类型
  const isGroupNode = (nodeId: string): boolean => {
    const dataToUse =
      fullGraphData.nodes.length > 0
        ? fullGraphData
        : lfRef.current?.getGraphData() || { nodes: [] };
    const node = dataToUse.nodes.find((n: any) => n.id === nodeId);
    const isGroup = node?.type === 'dynamic-group' || node?.type === 'group';
    console.log(
      `🔍 检查节点 ${nodeId} 是否为分组: ${isGroup} (type: ${node?.type})`
    );
    return isGroup;
  };

  // 获取节点所属的分组ID
  const getNodeGroup = (nodeId: string): string | null => {
    const dataToUse =
      fullGraphData.nodes.length > 0
        ? fullGraphData
        : lfRef.current?.getGraphData() || { nodes: [] };
    const node = dataToUse.nodes.find((n: any) => n.id === nodeId);
    return node?.group || node?.parentId || node?.groupId || null;
  };

  // 补全分组关系：确保分组的完整性
  const completeGroupRelations = (nodeIds: Set<string>): Set<string> => {
    const completed = new Set(nodeIds);
    const dataToUse =
      fullGraphData.nodes.length > 0
        ? fullGraphData
        : lfRef.current?.getGraphData() || { nodes: [] };

    console.log(`🔍 开始补全分组关系，初始节点数: ${nodeIds.size}`);

    // 遍历所有已选中的节点
    nodeIds.forEach(nodeId => {
      const node = dataToUse.nodes.find((n: any) => n.id === nodeId);
      console.log(
        `🔍 检查节点 ${nodeId}, 类型: ${node?.type}, 分组信息: group=${node?.group}, parentId=${node?.parentId}, groupId=${node?.groupId}`
      );

      // 情况1：如果是分组节点，添加所有子节点
      if (isGroupNode(nodeId)) {
        console.log(`📁 发现分组节点: ${nodeId}`);
        const children = getGroupChildren(nodeId);
        console.log(`📁 分组 ${nodeId} 的子节点:`, children);
        children.forEach(childId => {
          if (!completed.has(childId)) {
            completed.add(childId);
            console.log(`📁 补全分组 ${nodeId} 的子节点: ${childId}`);
          }
        });
      }
      // 情况2：如果是子节点，添加其分组和所有兄弟节点
      else {
        const groupId = getNodeGroup(nodeId);
        if (groupId) {
          console.log(`📁 发现子节点 ${nodeId} 属于分组 ${groupId}`);
          // 添加分组节点本身
          if (!completed.has(groupId)) {
            completed.add(groupId);
            console.log(`📁 补全子节点 ${nodeId} 的分组: ${groupId}`);
          }
          // 添加所有兄弟节点
          const siblings = getGroupChildren(groupId);
          console.log(`📁 分组 ${groupId} 的所有子节点:`, siblings);
          siblings.forEach(siblingId => {
            if (!completed.has(siblingId)) {
              completed.add(siblingId);
              console.log(`📁 补全子节点 ${nodeId} 的兄弟节点: ${siblingId}`);
            }
          });
        }
      }
    });

    console.log(`🎯 分组关系补全完成，最终节点数: ${completed.size}`);
    return completed;
  };

  // BFS算法获取指定跳数范围内的节点（支持分组逻辑）
  const getNodesWithinJumps = (
    startNodeId: string,
    maxJumps: number
  ): Set<string> => {
    console.log(`🔍 BFS搜索: 起始节点=${startNodeId}, 最大跳数=${maxJumps}`);

    const visited = new Set<string>();
    const queue: Array<{ nodeId: string; jumps: number }> = [
      { nodeId: startNodeId, jumps: 0 },
    ];

    // 第一步：标准BFS遍历
    while (queue.length > 0) {
      const { nodeId, jumps } = queue.shift()!;

      if (visited.has(nodeId) || jumps > maxJumps) continue;

      visited.add(nodeId);

      if (jumps < maxJumps) {
        // 找到所有相邻节点
        const connectedNodes = getConnectedNodes(nodeId);

        connectedNodes.forEach(connectedNodeId => {
          if (!visited.has(connectedNodeId)) {
            queue.push({ nodeId: connectedNodeId, jumps: jumps + 1 });
          }
        });
      }
    }

    console.log(`🎯 BFS基础结果: 共找到 ${visited.size} 个节点`);
    console.log(`🎯 BFS基础结果节点列表:`, Array.from(visited));

    // 第二步：补全分组关系
    const completedNodes = completeGroupRelations(visited);

    console.log(
      `🎯 补全分组后结果: 共 ${completedNodes.size} 个节点（+${completedNodes.size - visited.size}个补充节点）`
    );
    console.log(`🎯 最终结果节点列表:`, Array.from(completedNodes));
    return completedNodes;
  };

  // 获取与指定节点相连的所有节点（包括分组的特殊处理）
  const getConnectedNodes = (nodeId: string): string[] => {
    const connected: string[] = [];

    // 使用完整数据缓存而不是当前显示的数据
    const dataToUse =
      fullGraphData.edges.length > 0
        ? fullGraphData
        : lfRef.current?.getGraphData() || { edges: [] };

    console.log(`🔍 查找节点 ${nodeId} 的连接节点`);

    dataToUse.edges.forEach((edge: any) => {
      if (edge.sourceNodeId === nodeId) {
        connected.push(edge.targetNodeId);
        console.log(`🔗 找到出边: ${nodeId} -> ${edge.targetNodeId}`);
      } else if (edge.targetNodeId === nodeId) {
        connected.push(edge.sourceNodeId);
        console.log(`🔗 找到入边: ${edge.sourceNodeId} -> ${nodeId}`);
      }
    });

    // 如果当前节点是分组，还需要检查子节点的连接
    if (isGroupNode(nodeId)) {
      const children = getGroupChildren(nodeId);
      console.log(`📁 分组节点 ${nodeId} 的子节点:`, children);

      children.forEach(childId => {
        const childConnections = getDirectConnectedNodes(childId);
        childConnections.forEach(connectedId => {
          // 避免将分组内部的连接添加到外部连接中
          if (
            !children.includes(connectedId) &&
            connectedId !== nodeId &&
            !connected.includes(connectedId)
          ) {
            connected.push(connectedId);
            console.log(`🔗 通过子节点 ${childId} 找到连接: ${connectedId}`);
          }
        });
      });
    }

    console.log(`🎯 节点 ${nodeId} 的所有连接:`, connected);
    return connected;
  };

  // 获取直接连接的节点（不包括分组逻辑）
  const getDirectConnectedNodes = (nodeId: string): string[] => {
    const connected: string[] = [];
    const dataToUse =
      fullGraphData.edges.length > 0
        ? fullGraphData
        : lfRef.current?.getGraphData() || { edges: [] };

    dataToUse.edges.forEach((edge: any) => {
      if (edge.sourceNodeId === nodeId) {
        connected.push(edge.targetNodeId);
      } else if (edge.targetNodeId === nodeId) {
        connected.push(edge.sourceNodeId);
      }
    });

    return connected;
  };

  // 显示指定跳数范围的节点
  const showNodeWithJumps = (targetNodeId: string, jumps: number) => {
    if (!lfRef.current) return;

    console.log(
      `🎯 开始显示节点跳数范围: 目标节点=${targetNodeId}, 跳数=${jumps}`
    );

    // 注意：这里不能调用updateFullGraphData()，因为会覆盖完整数据缓存
    // 跳数切换时必须使用保存的完整数据，而不是当前显示的过滤数据

    console.log(
      `📊 完整图数据: 节点数=${fullGraphData.nodes.length}, 边数=${fullGraphData.edges.length}`
    );

    // 使用BFS算法找到指定跳数范围内的所有节点
    const visibleNodeIds = getNodesWithinJumps(targetNodeId, jumps);

    console.log(`📋 BFS结果: 应显示${visibleNodeIds.size}个节点`);
    console.log(`📋 可见节点IDs:`, Array.from(visibleNodeIds));

    // 从完整数据中过滤出可见的节点和边
    const visibleNodes = fullGraphData.nodes.filter(node =>
      visibleNodeIds.has(node.id)
    );
    const visibleEdges = fullGraphData.edges.filter(
      edge =>
        visibleNodeIds.has(edge.sourceNodeId) &&
        visibleNodeIds.has(edge.targetNodeId)
    );

    console.log(
      `📋 过滤结果: 可见节点=${visibleNodes.length}, 可见边=${visibleEdges.length}`
    );

    // 使用render方法重新渲染，只显示可见的节点和边
    lfRef.current.render({
      nodes: visibleNodes,
      edges: visibleEdges,
    });

    // 定位到目标节点，使用合适的缩放
    setTimeout(() => {
      if (lfRef.current) {
        try {
          // 优先使用focusOn定位到目标节点
          lfRef.current.focusOn(targetNodeId);

          // 限制缩放比例
          setTimeout(() => {
            const currentTransform = lfRef.current.getTransform();
            const currentScale =
              currentTransform?.SCALE_X || currentTransform?.scale || 1;

            if (currentScale > 1.2) {
              lfRef.current.zoom(1.2 / currentScale);
            }
          }, 50);
        } catch (error) {
          console.warn('定位节点失败:', error);
        }
      }
    }, 100);

    console.log(`✅ 完成${jumps}跳范围显示，共${visibleNodeIds.size}个节点`);
  };

  // 选择搜索结果 - 使用跳数搜索
  const selectSearchResult = (node: any) => {
    setCurrentSearchResult(node);
    setSearchKeyword(getDisplayName(node));
    setShowSearchResults(false);

    // 使用跳数搜索并显示
    performJumpSearch(node.id, jumpCount);
  };

  // 执行跳数搜索
  const performJumpSearch = (nodeId: string, jumps: number) => {
    if (!lfRef.current) return;

    try {
      const currentGraphData = lfRef.current.getGraphData();
      const result = jumpSearch(nodeId, jumps, currentGraphData);

      console.log(`🔍 执行跳数搜索: ${nodeId} (${jumps}跳)`);
      console.log(
        `📊 结果: ${result.visibleNodeIds.length}个节点, ${result.visibleEdgeIds.length}条边`
      );

      // 应用搜索结果
      filterVisibleElements(result.visibleNodeIds, result.visibleEdgeIds);

      // 定位到起始节点
      focusAndHighlightNode(nodeId);
    } catch (error) {
      console.error('跳数搜索失败:', error);
      // 失败时退回到简单定位
      focusAndHighlightNode(nodeId);
    }
  };

  // 定位并高亮指定节点
  const focusAndHighlightNode = (nodeId: string) => {
    if (!lfRef.current) return;

    console.log(`🎯 定位到节点: ${nodeId}`);

    try {
      // 定位到目标节点
      lfRef.current.focusOn(nodeId);

      // 限制缩放比例，避免过度放大
      setTimeout(() => {
        const currentTransform = lfRef.current.getTransform();
        const currentScale =
          currentTransform?.SCALE_X || currentTransform?.scale || 1;

        if (currentScale > 1.2) {
          lfRef.current.zoom(1.2 / currentScale);
        }
      }, 50);

      // TODO: 这里可以添加高亮效果，比如临时改变节点样式
      console.log(`✅ 成功定位到节点 ${nodeId}`);
    } catch (error) {
      console.warn('定位节点失败:', error);
    }
  };

  // 跳数变化处理
  const handleJumpCountChange = (value: number) => {
    setJumpCount(value);
    if (currentSearchResult) {
      showNodeWithJumps(currentSearchResult.id, value);
    }
  };

  // 清除搜索
  const handleClearSearch = () => {
    setSearchKeyword('');
    setSearchResults([]);
    setShowSearchResults(false);
    setCurrentSearchResult(null);

    console.log('🧹 搜索已清除');
  };

  // 监听搜索输入变化
  useEffect(() => {
    handleSearch();
  }, [searchKeyword]);

  // 检查LogicFlow API可用性
  const checkLogicFlowAPI = () => {
    if (!lfRef.current) {
      console.warn('❌ LogicFlow实例不存在');
      return false;
    }

    // 检查所有可能的显隐控制方法
    const possibleMethods = [
      'setNodeVisible',
      'setEdgeVisible',
      'getGraphData',
      'hideNode',
      'showNode',
      'hideEdge',
      'showEdge',
      'updateNodeVisible',
      'updateEdgeVisible',
      'setElementVisible',
      'hideElement',
      'showElement',
    ];

    const availableMethods = possibleMethods.filter(
      method => typeof lfRef.current[method] === 'function'
    );

    console.log('🔍 LogicFlow API 检查:');
    console.log('✅ 可用方法:', availableMethods);
    console.log(
      '❌ 不可用方法:',
      possibleMethods.filter(method => !availableMethods.includes(method))
    );

    // 打印所有方法供参考
    console.log('📋 LogicFlow实例的所有方法:');
    const allMethods = Object.getOwnPropertyNames(
      Object.getPrototypeOf(lfRef.current)
    )
      .filter(name => typeof lfRef.current[name] === 'function')
      .filter(
        name =>
          name.includes('visible') ||
          name.includes('hide') ||
          name.includes('show') ||
          name.includes('Node') ||
          name.includes('Edge')
      )
      .sort();
    console.log(allMethods);

    return availableMethods.length > 0;
  };

  // 全局方法：控制指定节点和边的可见性
  const filterVisibleElements = (
    visibleNodeIds: string[],
    visibleEdgeIds: string[]
  ) => {
    if (!lfRef.current) {
      console.warn('❌ LogicFlow实例不存在');
      return;
    }

    console.log(
      `🔍 设置可见元素: ${visibleNodeIds.length}个节点, ${visibleEdgeIds.length}条边`
    );

    try {
      const graphData = lfRef.current.getGraphData();
      console.log(
        `📊 当前图数据: ${graphData.nodes.length}个节点, ${graphData.edges.length}条边`
      );

      let successCount = 0;

      // 设置所有节点的可见性
      graphData.nodes.forEach((node: any) => {
        const shouldVisible = visibleNodeIds.includes(node.id);

        try {
          const nodeModel = lfRef.current.getNodeModelById(node.id);
          if (nodeModel) {
            // 直接设置visible属性
            nodeModel.visible = shouldVisible;
            //setProperty
            // nodeModel.setProperty('visible', shouldVisible);

            console.log(`节点 ${node.id}: ${shouldVisible ? '显示' : '隐藏'}`);
            successCount++;
          } else {
            console.warn(`❌ 无法找到节点 ${node.id} 的模型`);
          }
        } catch (nodeError) {
          console.error(`设置节点 ${node.id} 可见性失败:`, nodeError);
        }
      });

      // 设置所有边的可见性
      graphData.edges.forEach((edge: any) => {
        const shouldVisible = visibleEdgeIds.includes(edge.id);

        try {
          const edgeModel = lfRef.current.getEdgeModelById(edge.id);
          if (edgeModel) {
            // 直接设置visible属性
            edgeModel.visible = shouldVisible;
            // edgeModel.setProperty('visible', shouldVisible);
            console.log(`边 ${edge.id}: ${shouldVisible ? '显示' : '隐藏'}`);
            successCount++;
          } else {
            console.warn(`❌ 无法找到边 ${edge.id} 的模型`);
          }
        } catch (edgeError) {
          console.error(`设置边 ${edge.id} 可见性失败:`, edgeError);
        }
      });

      console.log(`✅ 元素可见性设置完成，成功设置 ${successCount} 个元素`);
    } catch (error) {
      console.error('设置元素可见性失败:', error);
    }
  };

  // 全局方法：显示所有元素
  const showAllElements = () => {
    if (!lfRef.current) {
      console.warn('LogicFlow实例不存在');
      return;
    }

    console.log('🔍 显示所有元素');

    try {
      const graphData = lfRef.current.getGraphData();
      let successCount = 0;

      // 显示所有节点
      graphData.nodes.forEach((node: any) => {
        try {
          const nodeModel = lfRef.current.getNodeModelById(node.id);
          if (nodeModel) {
            nodeModel.visible = true;
            successCount++;
          }
        } catch (error) {
          console.error(`显示节点 ${node.id} 失败:`, error);
        }
      });

      // 显示所有边
      graphData.edges.forEach((edge: any) => {
        try {
          const edgeModel = lfRef.current.getEdgeModelById(edge.id);
          if (edgeModel) {
            edgeModel.visible = true;
            successCount++;
          }
        } catch (error) {
          console.error(`显示边 ${edge.id} 失败:`, error);
        }
      });

      console.log(`✅ 所有元素已显示，成功设置 ${successCount} 个元素`);
    } catch (error) {
      console.error('显示所有元素失败:', error);
    }
  };

  // 挂载全局方法到window
  useEffect(() => {
    console.log('🚀 SearchPanel组件已挂载，开始初始化全局方法');

    const mountGlobalMethods = () => {
      if (typeof window !== 'undefined') {
        window.OPMSearchFilter = {
          filterVisible: filterVisibleElements,
          showAll: showAllElements,
          focusNode: focusAndHighlightNode,
          checkAPI: checkLogicFlowAPI,
          // 跳数搜索算法方法
          jumpSearch: (startNodeId: string, maxJumps: number) => {
            if (!lfRef.current) {
              console.warn('❌ LogicFlow实例不存在');
              return null;
            }

            try {
              const currentGraphData = lfRef.current.getGraphData();
              const result = jumpSearch(
                startNodeId,
                maxJumps,
                currentGraphData
              );

              console.log(
                `🎯 跳数搜索结果 (${startNodeId}, ${maxJumps}跳):`,
                result
              );

              // 可选：直接应用搜索结果
              // filterVisibleElements(result.visibleNodeIds, result.visibleEdgeIds);

              return result;
            } catch (error) {
              console.error('跳数搜索失败:', error);
              return null;
            }
          },

          // 跳数搜索并应用显示
          searchAndFilter: (startNodeId: string, maxJumps: number) => {
            if (!lfRef.current) {
              console.warn('❌ LogicFlow实例不存在');
              return;
            }

            try {
              const currentGraphData = lfRef.current.getGraphData();
              const result = jumpSearch(
                startNodeId,
                maxJumps,
                currentGraphData
              );

              console.log(
                `🔍 执行跳数搜索并过滤显示: ${startNodeId} (${maxJumps}跳)`
              );
              console.log(
                `📊 结果: ${result.visibleNodeIds.length}个节点, ${result.visibleEdgeIds.length}条边`
              );

              // 应用搜索结果
              filterVisibleElements(
                result.visibleNodeIds,
                result.visibleEdgeIds
              );

              // 定位到起始节点
              focusAndHighlightNode(startNodeId);

              return result;
            } catch (error) {
              console.error('跳数搜索并过滤失败:', error);
            }
          },

          // 测试算法
          testAlgorithm: testJumpSearch,

          debug: () => {
            console.log('🔍 OPMSearchFilter Debug Info:');
            console.log('LogicFlow instance:', lfRef.current);
            console.log(
              'Available methods:',
              window.OPMSearchFilter
                ? Object.keys(window.OPMSearchFilter)
                : 'Not mounted'
            );
            checkLogicFlowAPI();

            if (lfRef.current) {
              const graphData = lfRef.current.getGraphData();
              console.log('Graph data:', graphData);
              console.log(
                'Node IDs:',
                graphData.nodes.map((n: any) => n.id)
              );
              console.log(
                'Edge IDs:',
                graphData.edges.map((e: any) => e.id)
              );
            }
          },
        };
        console.log('🌍 全局搜索方法已挂载到 window.OPMSearchFilter');
        console.log('🌍 可用方法:', Object.keys(window.OPMSearchFilter));

        // 立即测试一下是否可访问
        setTimeout(() => {
          if (window.OPMSearchFilter) {
            console.log('✅ 全局方法验证成功，可以使用 window.OPMSearchFilter');
          } else {
            console.error('❌ 全局方法挂载失败');
          }
        }, 100);
      }
    };

    // 立即挂载（不等待lfRef）
    mountGlobalMethods();

    // 当lfRef可用时，更新方法
    if (lfRef.current) {
      console.log('🔗 LogicFlow实例已准备就绪，更新全局方法');
      mountGlobalMethods();
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.OPMSearchFilter;
        console.log('🗑️ 全局方法已清理');
      }
    };
  }, [lfRef.current]); // 依赖lfRef.current

  // 组件挂载时的调试日志
  useEffect(() => {
    console.log('🎯 SearchPanel组件已渲染，状态:');
    console.log('- lfRef.current:', lfRef.current);
    console.log('- graphData:', graphData);
    console.log('- window.OPMSearchFilter:', window.OPMSearchFilter);
  }, []);

  // 监听graphData变化，更新完整数据缓存
  useEffect(() => {
    if (graphData.nodes.length > 0 || graphData.edges.length > 0) {
      setFullGraphData({
        nodes: [...graphData.nodes],
        edges: [...graphData.edges],
      });
      console.log('从props更新完整数据缓存');
    }
  }, [graphData]);

  return (
    <Card
      title=""
      size="small"
      // extra={
      //   <Space>
      //     {isInSearchMode && (
      //       <>
      //         <Tooltip title="还原视图">
      //           <Button
      //             icon={<UndoOutlined />}
      //             size="small"
      //             onClick={restoreOriginalView}
      //           />
      //         </Tooltip>
      //         <Tooltip title="复位视图">
      //           <Button
      //             icon={<ClearOutlined />}
      //             size="small"
      //             onClick={onResetView}
      //           />
      //         </Tooltip>
      //       </>
      //     )}
      //   </Space>
      // }
    >
      <div className="space-y-3">
        {/* 搜索输入 */}
        <div className="search-container" style={{ position: 'relative' }}>
          <Input
            ref={searchInputRef}
            placeholder="输入节点名称搜索..."
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onFocus={() => setShowSearchResults(true)}
            onBlur={handleSearchBlur}
            suffix={
              <Button type="text" icon={<SearchOutlined />} size="small" />
            }
          />

          {/* 搜索结果下拉列表 */}
          {showSearchResults && searchResults.length > 0 && (
            <div
              className="search-results"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {searchResults.map(node => (
                <div
                  key={node.id}
                  onClick={() => selectSearchResult(node)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getNodeTypeColor(node.type),
                    }}
                  />
                  <span style={{ flex: 1, fontSize: '14px' }}>
                    {getDisplayName(node)}
                  </span>
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {getNodeTypeName(node.type)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {showSearchResults && searchKeyword && searchResults.length === 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                padding: '12px',
                textAlign: 'center',
                color: '#999',
              }}
            >
              未找到匹配的节点
            </div>
          )}
        </div>

        {/* 跳数控制 */}
        <div className="flex items-center space-x-2">
          <Text className="text-xs text-gray-600">跳数:</Text>
          <Select
            size="small"
            value={jumpCount}
            onChange={value => {
              setJumpCount(value);
              // 如果有当前搜索结果，重新执行搜索
              if (currentSearchResult) {
                performJumpSearch(currentSearchResult.id, value);
              }
            }}
            style={{ width: 60 }}
          >
            <Option value={1}>1</Option>
            <Option value={2}>2</Option>
            <Option value={3}>3</Option>
          </Select>
        </div>

        {/* 操作按钮 - 只有在有搜索时才显示 */}
        {(searchKeyword || currentSearchResult) && (
          <div className="flex items-center space-x-2">
            <Button
              size="small"
              onClick={handleClearSearch}
            >
              清除搜索
            </Button>

            <Button
              size="small"
              type="primary"
              onClick={() => window.OPMSearchFilter?.showAll()}
            >
              显示所有
            </Button>
          </div>
        )}

          {/* <Button
            size="small"
            onClick={() => {
              // 强制重新挂载全局方法
              if (typeof window !== 'undefined') {
                window.OPMSearchFilter = {
                  filterVisible: filterVisibleElements,
                  showAll: showAllElements,
                  focusNode: focusAndHighlightNode,
                  checkAPI: checkLogicFlowAPI,
                  jumpSearch: (startNodeId: string, maxJumps: number) => {
                    const currentGraphData = lfRef.current?.getGraphData();
                    if (!currentGraphData) return null;
                    return jumpSearch(startNodeId, maxJumps, currentGraphData);
                  },
                  searchAndFilter: (startNodeId: string, maxJumps: number) => {
                    const currentGraphData = lfRef.current?.getGraphData();
                    if (!currentGraphData) return;
                    const result = jumpSearch(
                      startNodeId,
                      maxJumps,
                      currentGraphData
                    );
                    filterVisibleElements(
                      result.visibleNodeIds,
                      result.visibleEdgeIds
                    );
                    focusAndHighlightNode(startNodeId);
                    return result;
                  },
                  testAlgorithm: testJumpSearch,
                  debug: () => {
                    console.log('🔍 OPMSearchFilter Debug Info:');
                    console.log('LogicFlow instance:', lfRef.current);
                    console.log(
                      'Available methods:',
                      window.OPMSearchFilter
                        ? Object.keys(window.OPMSearchFilter)
                        : 'Not mounted'
                    );

                    if (lfRef.current) {
                      const graphData = lfRef.current.getGraphData();
                      console.log('Graph data:', graphData);
                      console.log(
                        'Node IDs:',
                        graphData.nodes.map((n: any) => n.id)
                      );
                      console.log(
                        'Edge IDs:',
                        graphData.edges.map((e: any) => e.id)
                      );
                    }
                  },
                };
                console.log('🌍 手动挂载全局方法完成');
                alert('全局方法已挂载，请在控制台测试 window.OPMSearchFilter');
              }
            }}
          >
            挂载全局方法
          </Button> */}

        {/* 当前搜索结果信息 */}
        {currentSearchResult && (
          <div
            style={{
              padding: '8px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '4px',
              fontSize: '12px',
            }}
          >
            当前搜索: {getDisplayName(currentSearchResult)} ({jumpCount}跳范围)
          </div>
        )}

        {/* 调试信息 */}
        {/* <div
          style={{
            padding: '8px',
            backgroundColor: '#f0f0f0',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#666',
          }}
        >
          <div>🌍 全局测试方法:</div>
          <div>• window.OPMSearchFilter.testAlgorithm() - 测试算法</div>
          <div>• window.OPMSearchFilter.jumpSearch('nodeId', 2) - 算法计算</div>
          <div>• window.OPMSearchFilter.searchAndFilter('nodeId', 2) - 搜索并显示</div>
          <div>• window.OPMSearchFilter.filterVisible(['node1'], ['edge1']) - 显隐控制</div>
          <div>• window.OPMSearchFilter.focusNode('nodeId') - 定位节点</div>
          <div>• window.OPMSearchFilter.showAll() - 显示所有</div>
        </div> */}
      </div>
    </Card>
  );
};

export default SearchPanel;
